import { Heart, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { lc } from '@/i18n/copy';

const copy = {
  en: { empty: "You haven't saved a moment yet.", emptyDesc: "When something moves you… keep it.\nAnd don't forget to pass it on.", emptyCta: 'Send your first message' },
  es: { empty: 'Aún no has guardado un momento.', emptyDesc: 'Cuando algo te conmueve… guárdalo.\nY no olvides compartirlo.', emptyCta: 'Envía tu primer mensaje' },
  fr: { empty: "Vous n'avez pas encore sauvegardé de moment.", emptyDesc: "Quand quelque chose vous touche… gardez-le.\nEt n'oubliez pas de le transmettre.", emptyCta: 'Envoyez votre premier message' },
  pt: { empty: 'Você ainda não salvou um momento.', emptyDesc: 'Quando algo te emociona… guarde.\nE não esqueça de repassar.', emptyCta: 'Envie sua primeira mensagem' },
  ar: { empty: 'لم تحفظ أي لحظة بعد.', emptyDesc: 'عندما يؤثر فيك شيء… احتفظ به.\nولا تنسَ أن تمرره.', emptyCta: 'أرسل رسالتك الأولى' },
  ja: { empty: 'まだ瞬間を保存していません。', emptyDesc: '心が動いたとき…大切にしましょう。\nそして誰かに届けることも忘れずに。', emptyCta: '最初のメッセージを送る' },
  ko: { empty: '아직 저장한 순간이 없습니다.', emptyDesc: '마음이 움직일 때… 간직하세요.\n그리고 전달하는 것도 잊지 마세요.', emptyCta: '첫 번째 메시지 보내기' },
  id: { empty: 'Anda belum menyimpan momen.', emptyDesc: 'Saat sesuatu menggerakkan Anda… simpanlah.\nDan jangan lupa untuk meneruskannya.', emptyCta: 'Kirim pesan pertama Anda' },
  hi: { empty: 'आपने अभी तक कोई पल सहेजा नहीं है।', emptyDesc: 'जब कुछ आपको छू जाए… उसे संजो लें।\nऔर आगे बढ़ाना न भूलें।', emptyCta: 'अपना पहला संदेश भेजें' },
};

import type { Language } from '@/i18n/types';

export function SavedEmptyState({ language }: { language: Language }) {
  const c = lc(copy, language);

  return (
    <motion.div
      className="flex flex-col items-center justify-center text-center px-8 pt-24 lg:pt-32"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        className="relative mb-8"
        animate={{ scale: [1, 1.1, 1], opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Heart className="h-20 w-20 text-rose-400/50" fill="currentColor" />
        <div className="absolute inset-0 h-20 w-20 bg-rose-400/15 rounded-full blur-2xl" />
        <div className="absolute -inset-4 bg-rose-400/5 rounded-full blur-3xl" />
      </motion.div>
      <h2 className="text-xl font-bold text-white/90 mb-3">{c.empty}</h2>
      <p className="text-sm text-white/35 mb-10 max-w-[280px] leading-relaxed whitespace-pre-line">
        {c.emptyDesc}
      </p>
      <Link to="/create">
        <Button className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground rounded-full px-8 h-12 font-semibold shadow-lg shadow-primary/20 active:scale-[0.96] transition-transform">
          <Sparkles className="h-4 w-4 mr-2" />
          {c.emptyCta}
        </Button>
      </Link>
    </motion.div>
  );
}
